import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HelpCircle, BookA, Mic, MessageCircle, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Paywall from "@/components/Paywall";
import { useNarration } from "@/hooks/useNarration";
import { Loader } from "@/components/ProtectedRoute";
import Quiz from "@/pages/atelier/Quiz";
import Vocabulary from "@/pages/atelier/Vocabulary";
import Repeat from "@/pages/atelier/Repeat";
import Expression from "@/pages/atelier/Expression";

const TABS = [
  { id: "quiz", label: "Quiz", icon: HelpCircle, C: Quiz },
  { id: "vocab", label: "Vocabulaire", icon: BookA, C: Vocabulary },
  { id: "repeat", label: "Répète après moi", icon: Mic, C: Repeat, premium: true },
  { id: "expression", label: "Raconte !", icon: MessageCircle, C: Expression, premium: true },
];

function StoryPicker() {
  const [stories, setStories] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/stories").then((r) => setStories(r.data.filter((s) => s.status === "ready"))); }, []);
  if (!stories) return <Loader />;
  return (
    <div className="mt-8 grid gap-4 sm:grid-cols-2">
      {stories.map((s, i) => (
        <button key={s.id} data-testid={`atelier-pick-${s.id}`} onClick={() => navigate(s.locked ? "/premium" : `/atelier/${s.id}`)}
          style={{ animationDelay: `${i * 60}ms` }}
          className="rise card-paper flex items-center gap-4 p-3 text-left transition-transform hover:-translate-y-1">
          <img src={s.cover?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${s.cover}` : s.cover} alt="" className="h-20 w-28 rounded-2xl object-cover" />
          <span className="font-display text-lg font-bold">{s.title}</span>
          {s.locked && <Lock size={18} className="ml-auto shrink-0 text-[color:var(--muted-ink)]" />}
        </button>
      ))}
    </div>
  );
}

export default function Atelier() {
  const { id } = useParams();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [locked, setLocked] = useState(false);
  const [tab, setTab] = useState("quiz");
  const narration = useNarration();

  useEffect(() => {
    if (id) api.get(`/stories/${id}`).then((r) => setStory(r.data)).catch((e) => e?.response?.status === 402 && setLocked(true));
  }, [id]);
  useEffect(() => narration.stop(), [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = TABS.find((t) => t.id === tab);

  if (locked) return <div className="px-4 py-16"><Paywall title="Cette histoire est réservée à Premium" /></div>;

  return (
    <main data-testid="atelier-page" className="mx-auto max-w-5xl px-4 pb-24 pt-10 sm:px-8">
      <h1 className="rise font-display text-4xl font-bold sm:text-5xl">L'Atelier d'Expression</h1>
      {!id ? (
        <>
          <p className="mt-3 text-lg text-[color:var(--muted-ink)]">Choisis une histoire pour t'entraîner à comprendre, apprendre des mots et parler.</p>
          <StoryPicker />
        </>
      ) : !story ? <Loader /> : (
        <>
          <p className="mt-3 text-lg text-[color:var(--muted-ink)]">Autour de : <Link to={`/histoire/${story.id}`} className="font-bold text-[color:var(--terracotta)] underline">{story.title}</Link></p>
          <div className="mt-8 flex flex-wrap gap-2">
            {TABS.map(({ id: t, label, icon: Icon, premium }) => (
              <button key={t} data-testid={`atelier-tab-${t}`} onClick={() => setTab(t)}
                className={`chip flex items-center gap-2 !px-4 !py-2 !text-base ${tab === t ? "chip-active" : ""}`}><Icon size={18} /> {label}{premium && !user.is_premium && <Lock size={14} />}</button>
            ))}
          </div>
          <div className="mt-6">
            {current.premium && !user.is_premium
              ? <Paywall title={`« ${current.label} » est réservé à Premium`} text="Avec Premium, votre enfant s'entraîne à parler : il répète les phrases au micro et raconte avec ses mots, puis reçoit des encouragements personnalisés." />
              : <current.C key={tab} story={story} narration={narration} />}
          </div>
        </>
      )}
    </main>
  );
}
